import { useCallback, useEffect, useId, useRef, useState, type JSX } from 'react';
import { NavLink, useLocation } from 'react-router';

import { NAV_ITEMS } from '@/app/navigation';
import { CloseIcon, MenuIcon } from '@/components/ui/icons';
import { cn } from '@/lib/cn';

/**
 * A disclosure, not a modal dialog: the panel is rendered immediately after its
 * trigger in the DOM, so the natural tab order already does the right thing and
 * no focus trap is required.
 */
export function MobileNavigation(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const panelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { pathname } = useLocation();
  const [renderedPath, setRenderedPath] = useState(pathname);

  const close = useCallback((returnFocus = false) => {
    setIsOpen(false);
    if (returnFocus) {
      triggerRef.current?.focus();
    }
  }, []);

  // Navigating away should never leave the menu open behind the new page.
  // Adjusting during render rather than in an effect avoids a second pass, and
  // covers browser back/forward as well as clicks on the links below.
  if (renderedPath !== pathname) {
    setRenderedPath(pathname);
    setIsOpen(false);
  }

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        close(true);
      }
    };

    const handlePointerDown = (event: PointerEvent): void => {
      if (
        event.target instanceof Node &&
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [isOpen, close]);

  return (
    <div ref={containerRef} className="lg:hidden">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
        className="inline-flex size-11 items-center justify-center rounded-md border border-line bg-surface text-muted transition-colors duration-150 hover:bg-surface-muted hover:text-text"
      >
        {isOpen ? <CloseIcon /> : <MenuIcon />}
        <span className="sr-only">{isOpen ? 'Close menu' : 'Open menu'}</span>
      </button>

      <div
        id={panelId}
        hidden={!isOpen}
        className="absolute inset-x-0 top-full border-b border-line bg-surface shadow-card"
      >
        <nav aria-label="Primary" className="shell-container py-4">
          <ul className="flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    cn(
                      'flex min-h-11 items-center rounded-md px-3 text-base font-medium no-underline transition-colors duration-150',
                      isActive
                        ? 'bg-accent-soft text-accent-strong'
                        : 'text-muted hover:bg-surface-muted hover:text-text',
                    )
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </div>
  );
}
